/**
 * HIGH-PERFORMANCE CRASH GAME RENDERER
 *
 * Features:
 * - 60fps+ GPU-accelerated rendering
 * - Smooth exponential curve animation
 * - Particle effects
 * - Glow and trail effects
 * - Responsive design (mobile + desktop)
 * - Zero jank under load
 */

export interface RendererConfig {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

export interface RenderState {
  multiplier: number;
  isRunning: boolean;
  isCrashed: boolean;
  history: Array<{ x: number; y: number }>;
}

export class CrashRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number;
  private height: number;
  private dpr: number;

  // Animation state
  private animationFrame: number | null = null;
  private history: Array<{ x: number; y: number; multiplier: number }> = [];
  private particles: Array<Particle> = [];

  // Colors
  private readonly colorActive = "#10b981"; // Green
  private readonly colorCrashed = "#ef4444"; // Red
  private readonly colorGrid = "rgba(255, 255, 255, 0.1)";
  private readonly colorText = "#ffffff";

  constructor(config: RendererConfig) {
    this.canvas = config.canvas;
    this.width = config.width;
    this.height = config.height;
    this.dpr = window.devicePixelRatio || 1;

    // Get 2D context with alpha for transparency
    const ctx = this.canvas.getContext("2d", { alpha: true });
    if (!ctx) {
      throw new Error("Failed to get 2D context");
    }
    this.ctx = ctx;

    this.setupCanvas();
  }

  /**
   * Setup canvas with proper scaling for high-DPI displays
   */
  private setupCanvas(): void {
    // Set display size (CSS pixels)
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    // Set actual size in memory (scaled for DPR)
    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;

    // Scale context to match DPR
    this.ctx.scale(this.dpr, this.dpr);
  }

  /**
   * Starts rendering loop
   */
  start(): void {
    if (this.animationFrame) return;

    const render = () => {
      this.render();
      this.animationFrame = requestAnimationFrame(render);
    };

    render();
  }

  /**
   * Stops rendering loop
   */
  stop(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Updates multiplier and adds to history
   */
  updateMultiplier(multiplier: number, elapsed: number): void {
    const x = elapsed / 100; // Scale time to fit canvas
    const y = this.multiplierToY(multiplier);

    this.history.push({ x, y, multiplier });

    // Keep only recent history (last 10 seconds)
    if (this.history.length > 200) {
      this.history.shift();
    }

    // Add particle occasionally
    if (Math.random() < 0.1) {
      this.addParticle(x, y);
    }
  }

  /**
   * Triggers crash animation
   */
  crash(_finalMultiplier: number): void {
    // Create explosion particles
    const lastPoint = this.history[this.history.length - 1];
    if (lastPoint) {
      for (let i = 0; i < 30; i++) {
        this.addParticle(lastPoint.x, lastPoint.y, true);
      }
    }
  }

  /**
   * Resets renderer for new round
   */
  reset(): void {
    this.history = [];
    this.particles = [];
  }

  /**
   * Main render function
   */
  private render(): void {
    // Clear canvas
    this.ctx.fillStyle = "#0c1e2e";
    this.ctx.fillRect(0, 0, this.width, this.height);

    // Draw grid
    this.drawGrid();

    // Draw curve
    if (this.history.length > 1) {
      this.drawCurve();
    }

    // Draw particles
    this.updateAndDrawParticles();

    // Draw current multiplier
    if (this.history.length > 0) {
      const current = this.history[this.history.length - 1];
      this.drawMultiplierText(current.multiplier);
    }
  }

  /**
   * Draws background grid
   */
  private drawGrid(): void {
    this.ctx.strokeStyle = this.colorGrid;
    this.ctx.lineWidth = 1;

    // Vertical lines
    for (let x = 0; x < this.width; x += 50) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.height);
      this.ctx.stroke();
    }

    // Horizontal lines
    for (let y = 0; y < this.height; y += 50) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y);
      this.ctx.lineTo(this.width, y);
      this.ctx.stroke();
    }
  }

  /**
   * Draws the crash curve with glow effect
   */
  private drawCurve(): void {
    // const lastPoint = this.history[this.history.length - 1];
    const color = this.colorActive;

    // Draw glow (multiple passes for blur effect)
    for (let i = 0; i < 3; i++) {
      this.ctx.strokeStyle = `${color}${Math.floor(255 * (0.3 - i * 0.1))
        .toString(16)
        .padStart(2, "0")}`;
      this.ctx.lineWidth = 8 - i * 2;
      this.drawCurvePath();
    }

    // Draw main line
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    this.ctx.shadowColor = color;
    this.ctx.shadowBlur = 15;
    this.drawCurvePath();
    this.ctx.shadowBlur = 0;
  }

  /**
   * Draws the actual curve path
   */
  private drawCurvePath(): void {
    this.ctx.beginPath();
    this.ctx.moveTo(this.history[0].x, this.history[0].y);

    // Use quadratic curves for smoothness
    for (let i = 1; i < this.history.length - 1; i++) {
      const curr = this.history[i];
      const next = this.history[i + 1];
      const cpX = (curr.x + next.x) / 2;
      const cpY = (curr.y + next.y) / 2;
      this.ctx.quadraticCurveTo(curr.x, curr.y, cpX, cpY);
    }

    // Draw to last point
    const last = this.history[this.history.length - 1];
    this.ctx.lineTo(last.x, last.y);
    this.ctx.stroke();
  }

  /**
   * Draws current multiplier text
   */
  private drawMultiplierText(multiplier: number): void {
    const text = `${multiplier.toFixed(2)}x`;

    this.ctx.font = "bold 48px sans-serif";
    this.ctx.fillStyle = this.colorText;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";

    // Draw text with shadow
    this.ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    this.ctx.shadowBlur = 10;
    this.ctx.fillText(text, this.width / 2, 60);
    this.ctx.shadowBlur = 0;
  }

  /**
   * Converts multiplier to Y coordinate
   */
  private multiplierToY(multiplier: number): number {
    // Logarithmic scale for better visualization
    const logMultiplier = Math.log(multiplier);
    const maxLog = Math.log(10); // Show up to 10x in view
    const normalized = 1 - logMultiplier / maxLog;
    return Math.max(
      50,
      Math.min(this.height - 50, normalized * (this.height - 100) + 50),
    );
  }

  /**
   * Adds a particle effect
   */
  private addParticle(x: number, y: number, explosion = false): void {
    const particle: Particle = {
      x,
      y,
      vx: explosion ? (Math.random() - 0.5) * 10 : (Math.random() - 0.5) * 2,
      vy: explosion ? (Math.random() - 0.5) * 10 : -Math.random() * 3,
      life: 1.0,
      decay: explosion ? 0.02 : 0.01,
      color: explosion ? this.colorCrashed : this.colorActive,
    };

    this.particles.push(particle);
  }

  /**
   * Updates and renders particles
   */
  private updateAndDrawParticles(): void {
    this.particles = this.particles.filter((p) => {
      // Update physics
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.2; // Gravity
      p.life -= p.decay;

      // Draw particle
      if (p.life > 0) {
        this.ctx.fillStyle = `${p.color}${Math.floor(255 * p.life)
          .toString(16)
          .padStart(2, "0")}`;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        this.ctx.fill();
        return true;
      }

      return false;
    });
  }

  /**
   * Resizes canvas
   */
  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.setupCanvas();
  }

  /**
   * Destroys renderer and cleans up
   */
  destroy(): void {
    this.stop();
    this.history = [];
    this.particles = [];
  }
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  decay: number;
  color: string;
}
